import React, { useState, useEffect } from 'react';
import './LandingPage.css';

const LandingPage = ({ onGetStarted, onLogin, onSignUp }) => {
  const [activeTab, setActiveTab] = useState('home');
  const [heroSlide, setHeroSlide] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const tabs = [
    {
      id: 'home',
      label: 'Dashboard',
      image: '/screenshots/home.png',
      title: 'Everything You Need in One Place',
      description: 'Your dashboard shows your activity feed with GPS maps, recovery metrics from Oura, your next workout, and a countdown to race day.',
      features: [
        'Race countdown with days and weeks to go',
        'Oura integration for readiness, HRV, and sleep tracking',
        'Activity feed with GPS route maps'
      ]
    },
    {
      id: 'calendar',
      label: 'Training Plan',
      image: '/screenshots/calendar.png',
      title: 'Your Personal Training Plan',
      description: 'See your weekly pace distribution at a glance. Every workout is planned specifically for your goal race with periodized phases from base building through taper.',
      features: [
        'Weekly pace breakdown showing easy vs quality mileage',
        'Structured plans from 5K to ultra marathon',
        'Phase-based training: Foundation, Quality, Peak, Taper'
      ]
    },
    {
      id: 'workout',
      label: 'Workouts',
      image: '/screenshots/workout.png',
      title: 'Structured Workouts with Target Paces',
      description: 'Every workout comes with step-by-step structure and your personalized pace targets calculated using the Jack Daniels VDOT methodology.',
      features: [
        'Detailed workout structure with warmup, main set, and cooldown',
        'Personalized pace targets for every step',
        'One-click sync to your Garmin watch'
      ]
    },
  ];

  // Auto-rotate hero carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % tabs.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [tabs.length]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <div className="landing-page">
      {mobileMenuOpen && (
        <div className="nav-dropdown-backdrop" onClick={() => setMobileMenuOpen(false)} />
      )}
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="container">
          <div className="nav-content">
            <button onClick={scrollToTop} className="nav-logo-btn">
              <img src="/icon_logo.png" alt="AIthlete" className="nav-logo-img" />
            </button>
            <div className={`nav-links ${mobileMenuOpen ? 'open' : ''}`}>
              <div className="nav-mobile-links">
                <button onClick={() => scrollToSection('preview')} className="nav-link">Features</button>
                <button onClick={() => scrollToSection('integration')} className="nav-link">Watch Sync</button>
                <button onClick={() => scrollToSection('how-it-works')} className="nav-link">How It Works</button>
              </div>
              <div className="nav-auth-mobile">
                <button onClick={onLogin || onGetStarted} className="btn btn-login">Log In</button>
                <button onClick={onSignUp || onGetStarted} className="btn btn-signup">Sign Up</button>
              </div>
            </div>
            <div className="nav-auth">
              <button onClick={onLogin || onGetStarted} className="btn btn-login">Log In</button>
              <button onClick={onSignUp || onGetStarted} className="btn btn-signup">Sign Up</button>
            </div>
            <button
              className={`nav-hamburger ${mobileMenuOpen ? 'open' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <img src="/logo.png" alt="AIthlete - Train Smarter" className="hero-logo" />
              <p>
                Personalized training plans that adapt to your fitness, recovery, and progress — synced directly to your Smart Watch.
              </p>
              <div className="cta-group">
                <button onClick={onGetStarted} className="btn btn-primary">
                  Get Started
                </button>
                <button
                  onClick={() => scrollToSection('preview')}
                  className="btn btn-secondary"
                >
                  See It In Action
                </button>
              </div>
            </div>
            <div className="hero-visual">
              <div className="hero-carousel">
                <div className="hero-carousel-track" style={{ transform: `translateX(-${heroSlide * 100}%)` }}>
                  {tabs.map((tab) => (
                    <div key={tab.id} className="hero-carousel-slide">
                      <img
                        src={tab.image}
                        alt={tab.title}
                        className="hero-screenshot"
                      />
                    </div>
                  ))}
                </div>
                <div className="hero-carousel-dots">
                  {tabs.map((tab, index) => (
                    <button
                      key={tab.id}
                      className={`hero-carousel-dot ${heroSlide === index ? 'active' : ''}`}
                      onClick={() => setHeroSlide(index)}
                      aria-label={tab.label}
                    />
                  ))}
                </div>
                <div className="hero-carousel-label">{tabs[heroSlide].label}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Preview / Features Section */}
      <section className="app-preview" id="preview">
        <div className="container">
          <div className="preview-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`preview-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="preview-content">
            {tabs.map((tab) => (
              <div
                key={tab.id}
                className={`preview-panel ${activeTab === tab.id ? 'active' : ''}`}
              >
                <div className="preview-layout">
                  <div className="preview-screenshot-container">
                    <img
                      src={tab.image}
                      alt={tab.title}
                      className="preview-screenshot"
                    />
                  </div>
                  <div className="preview-info">
                    <h3>{tab.title}</h3>
                    <p>{tab.description}</p>
                    {tab.features && (
                      <ul className="preview-features">
                        {tab.features.map((feature, index) => (
                          <li key={index}>{feature}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Smart Device Integration Section */}
      <section className="integration" id="integration">
        <div className="container">
          <div className="integration-content">
            <div className="integration-text">
              <h2 className="section-title">Synced to<br />Your Smart Device</h2>
              <p className="integration-description">
                Have structured workouts delivered directly to your smart watch. Train with real-time pace guidance - no guessing, just run.
              </p>
              <ul className="benefits-list">
                <li>One-click workout sync to your device</li>
                <li>Structured workout steps with target paces</li>
                <li>Compatible with popular running watches</li>
                <li>Scheduled workouts appear on your calendar</li>
              </ul>
              <button onClick={onGetStarted} className="btn btn-primary">
                Get Started Free
              </button>
            </div>
            <div className="integration-visual">
              <img src="/watches.png" alt="Garmin and Apple Watch" className="watches-photo" />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works" id="how-it-works">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Your Plan<br />in Minutes</h2>
          </div>
          <div className="steps-container">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Enter Your Baseline</h3>
              <p>Tell us a recent race time or time trial. We'll calculate your exact fitness level and training paces.</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Set Your Goal</h3>
              <p>Choose your target race distance and date. Tell us about your experience and training preferences.</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Get Your Plan</h3>
              <p>Receive a fully personalized training program with exact intensity targets that adapt as you improve.</p>
            </div>
            <div className="step">
              <div className="step-number">4</div>
              <h3>Sync & Train</h3>
              <p>Connect your Garmin and have structured workouts delivered directly to your watch with real-time guidance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section footer-brand">
              <img src="/logo.png" alt="AIthlete" className="footer-logo" />
              <p>AI-powered training plans that adapt to you and sync to your Garmin.</p>
            </div>
            <div className="footer-section">
              <h4>Product</h4>
              <button onClick={() => scrollToSection('preview')}>Features</button>
              <button onClick={() => scrollToSection('how-it-works')}>How It Works</button>
            </div>
            <div className="footer-section">
              <h4>Company</h4>
              <a href="mailto:hello@aithlete.co.uk">Contact</a>
            </div>
          </div>
          <div className="copyright">
            <p>&copy; 2026 AIthlete. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
